import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { daDate } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DanishDatePicker } from "@/components/DanishDatePicker";
import { MultiSelect } from "@/components/MultiSelect";
import { UserSingleSelect } from "@/components/UserSingleSelect";
import { ClipboardCheck, Plus, Play, Pencil, FileText, Settings2, MapPin, CheckCircle2, Loader2, Power, AlertTriangle } from "lucide-react";

const STATUS_BADGE = {
  Planlagt: "bg-[#F4EFEC] text-[#806B61] border-[#E6DDD6]",
  "I gang": "bg-[#EDEAF7] text-[#5B4B8A] border-[#D8D0EC]",
  Afsluttet: "bg-[#E7EADF] text-[#5f6b4a] border-[#D3D8C6]",
};

function Card({ children, className = "" }) {
  return <div className={`bg-white rounded-2xl border border-[#EBE8E3] ${className}`}>{children}</div>;
}

export default function AuditPlan() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isAuditor = user?.role === "admin" || (user?.departments || []).includes("kvalitet");
  const [areas, setAreas] = useState([]);
  const [audits, setAudits] = useState([]);
  const [coverage, setCoverage] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planOpen, setPlanOpen] = useState(false);
  const [areaOpen, setAreaOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: "", planned_date: "", auditor_id: user?.id || "", area_ids: [], planned_points: "" });
  const [newArea, setNewArea] = useState("");
  const [saving, setSaving] = useState(false);

  const auditorUsers = users.filter((u) => u.role === "admin" || (u.departments || []).includes("kvalitet"));
  const activeAreas = areas.filter((a) => a.active !== false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, au, cov, us] = await Promise.all([
        api.get("/audit/areas"), api.get("/audit/audits"), api.get("/audit/coverage"), api.get("/audit/users"),
      ]);
      setAreas(a.data); setAudits(au.data); setCoverage(cov.data); setUsers(us.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { if (isAuditor) load(); else setLoading(false); }, []); // eslint-disable-line

  const openPlan = (a = null) => {
    if (a) {
      setEditId(a.id);
      setForm({ title: a.title || "", planned_date: a.planned_date || "", auditor_id: a.auditor_id || user?.id, area_ids: a.area_ids || [], planned_points: a.planned_points || "" });
    } else {
      setEditId(null);
      setForm({ title: "", planned_date: "", auditor_id: user?.id || "", area_ids: [], planned_points: "" });
    }
    setPlanOpen(true);
  };

  const savePlan = async () => {
    if (!form.title.trim()) return toast.error("Angiv en titel");
    if (!form.area_ids.length) return toast.error("Vælg mindst ét område");
    setSaving(true);
    try {
      if (editId) await api.patch(`/audit/audits/${editId}`, form);
      else await api.post("/audit/audits", form);
      toast.success(editId ? "Plan opdateret" : "Audit planlagt");
      setPlanOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const startAudit = async (a) => {
    try {
      await api.post(`/audit/audits/${a.id}/start`);
      nav(`/kvalitet/audit/${a.id}/udfoer`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addArea = async () => {
    if (!newArea.trim()) return;
    try { await api.post("/audit/areas", { name: newArea.trim() }); setNewArea(""); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleArea = async (a) => {
    try { await api.patch(`/audit/areas/${a.id}`, { active: a.active === false }); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!isAuditor) return <div className="text-stone-500" data-testid="audit-plan-noaccess">Kun kvalitet og administratorer har adgang til auditplanen.</div>;

  const upcoming = audits.filter((a) => a.status !== "Afsluttet");

  return (
    <div className="space-y-8" data-testid="audit-plan-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="label-caps flex items-center gap-2 text-[#806B61]"><ClipboardCheck className="h-3.5 w-3.5" /> Kvalitet · Intern audit</p>
          <h1 className="text-4xl font-semibold text-[#1A1919] mt-1">Auditplan</h1>
          <p className="text-stone-500 mt-2 max-w-2xl">Planlæg audits, start eller fortsæt gennemførelsen, og hold øje med hvornår hvert område sidst er auditeret.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="audit-manage-areas-btn" onClick={() => setAreaOpen(true)} className="rounded-xl border-[#EBE8E3]"><Settings2 className="h-4 w-4 mr-1.5" /> Områder</Button>
          <Button data-testid="audit-plan-new-btn" onClick={() => openPlan()} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]"><Plus className="h-4 w-4 mr-1.5" /> Planlæg audit</Button>
        </div>
      </div>

      {loading ? <div className="flex items-center gap-2 text-stone-400"><Loader2 className="h-4 w-4 animate-spin" /> Indlæser…</div> : (
      <>
      {/* Kommende & igangværende */}
      <section className="space-y-3" data-testid="audit-upcoming">
        <h2 className="text-lg font-medium text-[#1A1919]">Kommende & igangværende</h2>
        {upcoming.length === 0 ? (
          <Card className="p-8 text-center text-stone-400" >Ingen planlagte audits endnu. Tryk "Planlæg audit" for at komme i gang.</Card>
        ) : upcoming.map((a) => (
          <Card key={a.id} className="p-5" >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-mono text-stone-400">{a.audit_no}</span>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border ${STATUS_BADGE[a.status]}`}>{a.status}</span>
                </div>
                <p className="text-base font-semibold text-[#1A1919] mt-1">{a.title}</p>
                <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-stone-600">
                  <span>Planlagt: <span className="font-medium">{daDate(a.planned_date)}</span></span>
                  <span>Auditor: <span className="font-medium">{a.auditor_name || "—"}</span></span>
                  <span>Områder: <span className="font-medium">{(a.area_reviews || []).map((r) => r.area_name).join(", ") || "—"}</span></span>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                {a.status === "Planlagt" && <>
                  <Button variant="outline" data-testid={`audit-edit-${a.id}`} onClick={() => openPlan(a)} className="rounded-xl border-[#EBE8E3]"><Pencil className="h-4 w-4 mr-1.5" /> Redigér plan</Button>
                  <Button data-testid={`audit-start-${a.id}`} onClick={() => startAudit(a)} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]"><Play className="h-4 w-4 mr-1.5" /> Start audit</Button>
                </>}
                {a.status === "I gang" && <Button data-testid={`audit-continue-${a.id}`} onClick={() => nav(`/kvalitet/audit/${a.id}/udfoer`)} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]"><Play className="h-4 w-4 mr-1.5" /> Fortsæt audit</Button>}
              </div>
            </div>
          </Card>
        ))}
      </section>

      {/* Områdedækning */}
      <section className="space-y-3" data-testid="audit-coverage">
        <h2 className="text-lg font-medium text-[#1A1919] flex items-center gap-2"><MapPin className="h-4 w-4 text-[#806B61]" /> Områdedækning</h2>
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#FBFAF8] text-stone-500 text-xs uppercase tracking-wide">
              <tr><th className="text-left px-4 py-3 font-medium">Område</th><th className="text-left px-4 py-3 font-medium">Seneste gennemførte audit</th><th className="text-left px-4 py-3 font-medium">Næste planlagte</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody>
              {coverage.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-stone-400">Ingen aktive områder. Tilføj områder via "Områder".</td></tr>}
              {coverage.map((c) => (
                <tr key={c.area_id} className="border-t border-[#F0EDE8]" data-testid={`coverage-row-${c.area_id}`}>
                  <td className="px-4 py-3 font-medium text-[#1A1919]">{c.area_name}</td>
                  <td className="px-4 py-3 text-stone-600">{c.last_completed ? <span>{daDate(c.last_completed.actual_date)} <span className="text-stone-400">· {c.last_completed.audit_no}</span></span> : <span className="text-stone-400">Ikke registreret</span>}</td>
                  <td className="px-4 py-3 text-stone-600">{c.next_planned ? <span className="inline-flex items-center gap-2 flex-wrap">{daDate(c.next_planned.planned_date)} <span className="text-stone-400">· {c.next_planned.audit_no}</span>{c.next_planned.overdue && <span data-testid={`coverage-overdue-${c.area_id}`} title="Den planlagte auditdato er passeret" className="inline-flex items-center gap-1 rounded-md bg-[#F1DEDA] text-[#A83232] border border-[#E9C9C3] px-1.5 py-0.5 text-[10px] font-semibold"><AlertTriangle className="h-3 w-3" strokeWidth={2} /> Overskredet</span>}</span> : <span className="text-[#A83232] font-medium">Mangler plan</span>}</td>
                  <td className="px-4 py-3 text-right">{c.last_completed && <Button size="sm" variant="ghost" data-testid={`coverage-result-${c.area_id}`} onClick={() => nav(`/kvalitet/audit/${c.last_completed.audit_id}/rapport`)} className="text-[#806B61] hover:bg-[#F3F1EC]"><FileText className="h-4 w-4 mr-1" /> Se resultat</Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </section>
      </>
      )}

      {/* Plan dialog */}
      <Dialog open={planOpen} onOpenChange={setPlanOpen}>
        <DialogContent className="max-w-lg rounded-2xl" data-testid="audit-plan-dialog">
          <DialogHeader><DialogTitle>{editId ? "Redigér plan" : "Planlæg audit"}</DialogTitle><DialogDescription>Udfyld titel, dato, auditor og vælg de områder auditten dækker.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-1">
            <div><label className="text-xs font-medium text-stone-600 mb-1 block">Titel</label>
              <Input data-testid="audit-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="F.eks. Intern audit – Montage & svejsning" className="rounded-xl" /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-stone-600 mb-1 block">Planlagt dato</label>
                <DanishDatePicker value={form.planned_date} onChange={(v) => setForm({ ...form, planned_date: v })} allowClosed testid="audit-form-date" /></div>
              <div><label className="text-xs font-medium text-stone-600 mb-1 block">Auditor</label>
                <UserSingleSelect users={auditorUsers} value={form.auditor_id} onChange={(v) => setForm({ ...form, auditor_id: v })} allowNone={false} placeholder="Vælg auditor" testid="audit-form-auditor" className="h-10" /></div>
            </div>
            <div><label className="text-xs font-medium text-stone-600 mb-1 block">Områder</label>
              <MultiSelect options={activeAreas.map((a) => ({ value: a.id, label: a.name }))} selected={form.area_ids} onChange={(v) => setForm({ ...form, area_ids: v })} placeholder="Vælg områder" testid="audit-form-areas" />
              {activeAreas.length === 0 && <p className="text-xs text-[#A83232] mt-1">Opret først områder under "Områder".</p>}</div>
            <div><label className="text-xs font-medium text-stone-600 mb-1 block">Planlagte auditpunkter / krav (valgfrit)</label>
              <Textarea data-testid="audit-form-points" value={form.planned_points} onChange={(e) => setForm({ ...form, planned_points: e.target.value })} className="rounded-xl min-h-[70px]" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanOpen(false)} className="rounded-xl">Annullér</Button>
            <Button data-testid="audit-form-save" onClick={savePlan} disabled={saving} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]">{saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Gem</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Områder dialog */}
      <Dialog open={areaOpen} onOpenChange={setAreaOpen}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="audit-areas-dialog">
          <DialogHeader><DialogTitle>Vedligehold områder</DialogTitle><DialogDescription>Opret nye områder eller deaktivér områder der ikke længere auditeres.</DialogDescription></DialogHeader>
          <div className="flex gap-2">
            <Input data-testid="area-new-input" value={newArea} onChange={(e) => setNewArea(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addArea()} placeholder="Nyt område…" className="rounded-xl" />
            <Button data-testid="area-add-btn" onClick={addArea} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]"><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-[#F0EDE8] mt-2">
            {areas.length === 0 && <p className="text-sm text-stone-400 py-4 text-center">Ingen områder endnu.</p>}
            {areas.map((a) => (
              <div key={a.id} className="flex items-center justify-between py-2.5" data-testid={`area-row-${a.id}`}>
                <span className={`text-sm ${a.active === false ? "text-stone-400 line-through" : "text-[#1A1919]"}`}>{a.name}</span>
                <Button size="sm" variant="ghost" data-testid={`area-toggle-${a.id}`} onClick={() => toggleArea(a)} className={a.active === false ? "text-stone-400" : "text-[#5f6b4a]"}>
                  <Power className="h-4 w-4 mr-1" /> {a.active === false ? "Aktivér" : "Deaktivér"}
                </Button>
              </div>
            ))}
          </div>
          <p className="text-xs text-stone-400">Deaktiverede områder forsvinder fra nye planer, men bevares i tidligere rapporter.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
